import { getUserAttributes } from '@noths/polaris-client-user-configuration';

import { getStoredGuestIdToken } from 'src/services/auth/storedGuestIdToken';

/**
 * Resolves the ID JWT used as `x-id-token` for checkout/basket GraphQL.
 * Tries `Auth.currentSession()` first (reliable when `currentAuthenticatedUser`
 * omits `signInUserSession`), then `getUserAttributes().idToken`, then finally the
 * express guest token stored under `noths.idtoken` by the bespoke guestAuth module
 * (Cognito is unaware of it), so express pay and the confirmation fetch authenticate
 * as the minted guest. That last one is read through getStoredGuestIdToken, which drops it once it
 * has expired rather than handing back a token the API will reject.
 */
export async function getBasketIdToken(): Promise<string | undefined> {
  const auth = window.Amplify?.default?.Auth as
    | {
        currentSession?: () => Promise<unknown>;
      }
    | undefined;

  if (auth && typeof auth.currentSession === 'function') {
    try {
      const session = (await auth.currentSession()) as {
        getIdToken?: () => { getJwtToken: () => string };
        idToken?: { jwtToken?: string };
      };

      if (typeof session.getIdToken === 'function') {
        const jwt = session.getIdToken().getJwtToken();
        if (jwt) {
          return jwt;
        }
      }

      const jwt = session.idToken?.jwtToken;
      if (jwt) {
        return jwt;
      }
    } catch {
      /* unauthenticated or session unavailable */
    }
  }

  const attributes = await getUserAttributes();
  if (attributes?.idToken) {
    return attributes.idToken;
  }

  return getStoredGuestIdToken() ?? undefined;
}
